import React, { useContext, useEffect } from "react";
import { AppTitleContext } from "app_context";
import { Link, useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useGetMachineById } from "hooks/machines/useGetMachineById";
import { styled } from "@mui/material/styles";
import { MachineStatusKPI } from "components/OverviewPages/KPIs/MachineStatusKPI";
import { FleetCaseMeanResponseTimeKpi } from "components/OverviewPages/KPIs/CaseMeanTimeKpi";
import { FleetCaseSummaryKpi } from "components/OverviewPages/KPIs/CaseSummaryKpi";
import { FleetSavingsKpi } from "components/OverviewPages/KPIs/SavingsKpi";
import { Breadcrumbs } from "components/Breadcrumbs";
import { MachineTable } from "components/OverviewPages/MachineTable";
import { Grid, Paper, Skeleton } from "@mui/material";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import { OverviewMapPanel } from "components/OverviewPages/Map/OverviewMapPanel";
import { useTrackPageView } from "hooks/useTrackPageView";
import { isNil } from "lodash";

export function MachinePage() {
  useTrackPageView("Machine");
  const appTitleContext = useContext(AppTitleContext);
  const history = useHistory();
  const { machineId: id } = useParams();
  const machineId = Number(id);

  const {
    loading: machineLoading,
    error: machineError,
    machine
  } = useGetMachineById(machineId);

  const { loading: componentsLoading, response: componentsData } = useFetchGet(
    `machine_overview/${machineId}`
  );

  const { loading: machinesLoading, response: machineData } = useFetchGet(
    `site_overview/${machine?.site}`,
    isNil(machine?.site)
  );

  useEffect(() => {
    if (machineError) {
      history.replace("/notfound");
    }
  }, [machineError, history]);

  useEffect(() => {
    appTitleContext.setPageTitle(
      <Breadcrumbs>
        <Link to={`/pages/fleet`}>Fleet</Link>
        <Link to={`/pages/site/${machine?.site}`}>{machine?.site_name}</Link>
        <div>{machine?.name}</div>
      </Breadcrumbs>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine]);

  // Remap data for map component
  const mappedMapValues =
    machineData?.map(result => {
      return {
        latitude: result?.latitude,
        longitude: result?.longitude,
        name: result?.name,
        isMarkerSelected: (marker, selectedMarker) =>
          marker && selectedMarker && marker === selectedMarker,
        marker: result?.name
      };
    }) ?? [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={3}>
        <MachineStatusKPI
          site={{ id: machine?.site, name: machine?.site_name }}
          machine={machine}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetCaseMeanResponseTimeKpi expanded machine={machine} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetCaseSummaryKpi expanded machine={machine} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FleetSavingsKpi expanded machine={machine} />
      </Grid>
      <Grid item xs={12} md={8}>
        <TableContainer>
          {componentsLoading || machineLoading ? (
            <FleetTableSkeletonWrapper>
              <FleetTableSkeleton />
            </FleetTableSkeletonWrapper>
          ) : (
            <MachineTable
              loading={componentsLoading || machineLoading}
              machineTableData={componentsData ?? []}
              selectedMachineName={machine?.name}
            />
          )}
        </TableContainer>
      </Grid>
      <Grid item xs={12} md={4}>
        {machinesLoading || mappedMapValues.length === 0 ? (
          <FleetMapSkeletonWrapper>
            <FleetMapSkeleton />
          </FleetMapSkeletonWrapper>
        ) : (
          <OverviewMapPanel
            data={mappedMapValues}
            selectedMarker={machine?.name}
          />
        )}
      </Grid>
    </Grid>
  );
}

const TableContainer = styled(Paper)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 8rem - 123px);
`;

const FleetTableSkeletonWrapper = styled("div")`
  display: flex;
  justify-content: center;
  height: 100%;
`;

const FleetTableSkeleton = styled(Skeleton)`
  transform: none;
  height: 50%;
  width: 97%;
  margin-top: 15px;
`;

const FleetMapSkeletonWrapper = styled("div")`
  height: 100%;
`;

const FleetMapSkeleton = styled(Skeleton)`
  transform: none;
  height: 100%;
`;
